import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1TutoraiConfigMemoryQueryOptions,
  useGetApiV1TutoraiConfigMemory,
  usePutApiV1TutoraiConfigMemory,
} from '@/shared/api/generated';

import type { TutorPromptScope } from '../types/tutorAi';
import { type TutorPromptFieldState, usePromptDraft } from './usePromptDraft';

/** Params do GET de config no escopo pedido. `undefined` significa escopo global. */
function buildQueryParams(scope: TutorPromptScope) {
  return scope.kind === 'class' ? { classStudentId: scope.classStudentId } : undefined;
}

/**
 * Campo de memória do tutor no escopo global ou de uma turma.
 *
 * A memória carrega o conhecimento fixo que o assistente deve tratar como dado — o que
 * ele sabe, não como ele fala. Salvar invalida a query do mesmo escopo.
 *
 * @param scope - Escopo global ou de turma
 * @returns Estado do campo e ações de salvar/descartar
 *
 * @example
 * ```tsx
 * const memory = useTutorMemoryConfig({ kind: 'class', classStudentId: 9020 });
 * ```
 */
export function useTutorMemoryConfig(scope: TutorPromptScope): TutorPromptFieldState {
  const queryClient = useQueryClient();
  const params = buildQueryParams(scope);

  const query = useGetApiV1TutoraiConfigMemory(params);
  const mutation = usePutApiV1TutoraiConfigMemory({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries(getApiV1TutoraiConfigMemoryQueryOptions(params));
      },
    },
  });

  return usePromptDraft(query, mutation, scope);
}
